import { Injectable } from '@angular/core';
import { AdminDashboardService } from './admin-dashboard.service';
import { DashboardCalendarBase, DashboardCalendarCount, DashboardCalendarChartCount, DashboardCalendarChartPercentage, DashboardCalendarChartStats, DashboardCalendarPencentage, DashboardCalendarStats } from 'app/model/dashboardCalendar';
import { Observable } from 'rxjs';
import { LoginService } from '../../login/login.service';
import { Chart } from 'app/model/chart';
import * as Chartist from 'chartist';
import ChartistTooltip from 'chartist-plugin-tooltips-updated';
import swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class DashboardCalendarDonutStatsService {
  private readonly ACCEPTED_PREFIX: string = 'accepted'
  private readonly PENDING_PREFIX: string = 'pending'

  private readonly HOLIDAYS_SUFFIX: string = `_holidays`
  private readonly PERSONAL_MATTERS_SUFFIX: string = `_personal_matters`
  private readonly SICK_LEAVE_SUFFIX: string = `_sick_leave`
  private readonly PAID_LEAVE_SUFFIX: string = `_paid_leave`
  private readonly REMOTE_WORK_SUFFIX: string = `_remote_work`

  /**
  * Constructor
  *
  * @param {AdminDashboardService} adminDashboardService
  * @param {LoginService} loginService
  * 
  */
  constructor(
    private adminDashboardService: AdminDashboardService,
    private loginService: LoginService
  ) { }

  public getDashboardCalendarStats(
    selectedDptId: number | null,
    selectedEmployeeId: number | null,
    startDate: Date | null,
    endDate: Date | null
  ): Observable<DashboardCalendarStats> {
    const isSuper = this.isSuper()

    if (selectedEmployeeId)
      return this.adminDashboardService.getDashboardCalendarStatsByDateRangeAndEmployeeId(selectedEmployeeId, startDate, endDate)

    return isSuper && !selectedDptId
      ? this.adminDashboardService.getDashboardCalendarStatsByDateRange(startDate, endDate)
      : this.adminDashboardService.getDashboardCalendarStatsByDateRangeAndDptId(selectedDptId, startDate, endDate)
  }

  private isSuper(): boolean {
    return this.loginService.isAdmin() || this.loginService.isSuperLeader()
  }

  public mapDonutStats(totals: DashboardCalendarCount, percentages: DashboardCalendarPencentage, isAccepted: boolean)
    : DashboardCalendarChartStats {
    const statusPrefix = isAccepted ? this.ACCEPTED_PREFIX : this.PENDING_PREFIX

    const donutStats = new DashboardCalendarChartStats()
    donutStats.totals = this.mapDonutTotals(totals, statusPrefix)
    donutStats.percentages = this.mapDonutPercentages(percentages, statusPrefix)

    return donutStats
  }

  private mapDonutTotals(totals: DashboardCalendarCount, statusPrefix: string): DashboardCalendarChartCount {
    const values = this.mapDonut(totals, statusPrefix)

    const totalCount = totals[`total_${statusPrefix}`]

    return new DashboardCalendarChartCount(...values, totalCount)
  }

  private mapDonutPercentages(percentages: DashboardCalendarPencentage, statusPrefix: string): DashboardCalendarChartPercentage {
    const values = this.mapDonut(percentages, statusPrefix)

    return new DashboardCalendarChartPercentage(...values)
  }

  private mapDonut<T extends DashboardCalendarBase>(values: T, statusPrefix: string): [number, number, number, number, number] {
    return [
      this.getStatsValue(values, statusPrefix, this.HOLIDAYS_SUFFIX),
      this.getStatsValue(values, statusPrefix, this.PERSONAL_MATTERS_SUFFIX),
      this.getStatsValue(values, statusPrefix, this.SICK_LEAVE_SUFFIX),
      this.getStatsValue(values, statusPrefix, this.PAID_LEAVE_SUFFIX),
      this.getStatsValue(values, statusPrefix, this.REMOTE_WORK_SUFFIX),
    ]
  }

  private getStatsValue<T extends DashboardCalendarBase>(values: T, prefix: string, suffix: string): number {
    return values[`${prefix}${suffix}`]
  }

  public mapData(totals: DashboardCalendarChartCount): Chartist.IChartistData {
    return {
      series: [
        { value: totals.totalHolidays, className: 'ct-holidays', meta: 'Vacaciones|bg-holidays' },
        { value: totals.totalPersonalMatters, className: 'ct-personal-matters', meta: 'Asuntos Propios|bg-personal-matters' },
        { value: totals.totalSickLeave, className: 'ct-sick-leave', meta: 'Bajas|bg-sick-leave' },
        { value: totals.totalPaidLeave, className: 'ct-paid-leave', meta: 'Permisos Retribuidos|bg-paid-leave' },
        { value: totals.totalRemoteWork, className: 'ct-remote-work', meta: 'Teletrabajo|bg-remote-work' }
      ].sort((a, b) => a.value - b.value)
    }
  }

  public createChart(data: any, totals: DashboardCalendarCount, isAccepted: boolean): Chart {
    return {
      type: 'Pie',
      data: data,
      options: {
        donut: true,
        startAngle: 0,
        labelInterpolationFnc: () => isAccepted ? totals.total_accepted : totals.total_pending,
        plugins: [
          ChartistTooltip({ // Agrega tooltips al pasar por encima
            appendToBody: true,
            tooltipFnc: (meta, value) => this.generateTooltip(meta, value)
          })
        ]
      },
      events: {
        draw(data: any): void {
          if (data.type === 'label') { // Si el elemento es una etiqueta
            if (data.index === 0) { // Si es la primera etiqueta (la que aparece en el centro)
              data.element.attr({ // Se desplaza al centro
                dx: data.element.root().width() / 2,
                dy: data.element.root().height() / 1.9,
                'style': 'font-weight: bold'
              })
            } else { // Si no lo es, se elimina
              data.element.remove()
            }
          }

          if (data.type === 'slice' && data.series.value !== 0) {
            const stokeWidth = 8 + (data.index * 8)

            const pathLength = data.element._node.getTotalLength()

            data.element.attr({
              'stroke-dasharray': pathLength + 'px ' + pathLength + 'px',
              'stroke-dashoffset': -pathLength + 'px',
              'style': `stroke-width: ${stokeWidth}px`
            })

            // Animación SVG
            data.element.animate({
              'stroke-dashoffset': {
                begin: 150 * data.index,   // retraso progresivo según index
                dur: 2000,
                from: -pathLength + 'px',
                to: '0px',
                easing: Chartist.Svg.Easing.easeInOutCubic,
                fill: 'freeze'
              }
            }, false)
          }
        }
      }
    }
  }

  private generateTooltip(meta: string, value: number): string {
    const [name, className] = meta.split('|')

    return `<div class="tooltip-content">
                <div class="tooltip-color ${className}"></div>
                <span><strong>${name}:</strong> ${value}</span>
              </div>`
  }

  public fireSwalAlert(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return swal.fire(options)
  }
}
